/**
 * Guest Identity Context - Provides guest tracking throughout the app
 * 
 * Wraps the app to provide:
 * - Automatic guest session creation
 * - Page view tracking
 * - Merge on registration/login
 */

import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGuestIdentity, trackPageView } from '../hooks/useGuestIdentity';

const GuestIdentityContext = createContext(null);

export function GuestIdentityProvider({ children }) {
  const location = useLocation();
  const guestIdentity = useGuestIdentity();
  const lastTrackedPath = useRef(null);
  
  // Track page views on route change
  useEffect(() => {
    // Don't track if:
    // - No guest ID yet
    // - Guest is blocked
    // - Same path as last tracked (avoid duplicates on re-renders)
    if (!guestIdentity.guestId || guestIdentity.isBlocked) return;
    if (lastTrackedPath.current === location.pathname) return;
    
    lastTrackedPath.current = location.pathname;
    
    // Track the page view
    trackPageView(location.pathname);
  }, [location.pathname, guestIdentity.guestId, guestIdentity.isBlocked]);
  
  return (
    <GuestIdentityContext.Provider value={guestIdentity}>
      {children}
    </GuestIdentityContext.Provider>
  );
}

export function useGuestContext() {
  const context = useContext(GuestIdentityContext);
  if (!context) {
    // Return a stub if used outside provider (during SSR or tests)
    return {
      guestId: null,
      isVerified: false,
      isBlocked: false,
      visitCount: 0,
      loading: true,
      initSession: async () => null,
      trackAction: async () => {},
      mergeToUser: async () => null,
      checkStatus: async () => ({ allowed: true })
    };
  }
  return context;
}

export default GuestIdentityProvider;
